// src/pages/CharacterSwitch.tsx（完全版：キャラクター切替後に「現在選択中」へスクロール）
//
// 追加点：
// - const currentSectionRef = useRef<HTMLElement | null>(null)
// - onSelectCharacter 成功時に currentSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })
// - ヘッダー固定分のズレ対策で section に scroll-mt-20 を付与

import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { resolveBackendAssetUrl } from "../lib/backendAsset";
import { logError, toUserMessage } from "../lib/errors";
import { storage } from "../lib/storage";
import { getDeviceParams } from "../lib/device";

type CharacterVM = {
  id: string;
  name: string;
  description: string;
  initial: string;
  voiceId?: string;
  voiceName?: string;
  voiceFix?: number; // 1 なら音声変更不可
  iconUrl?: string; // キャラクターアイコン画像URL
};

type VoiceVM = {
  id: string;
  name: string;
};

type DebugInfo = {
  time: string;
  phase: string;
  auth: ReturnType<typeof storage.getAuth>;
  device: ReturnType<typeof getDeviceParams>;
  note?: string;
  resCharacter?: any;
  resVoice?: any;
  normalized?: any;
};

function getByPath(obj: any, path: string): any {
  if (!obj) return undefined;
  const parts = path.split(".");
  let cur = obj;
  for (const p of parts) {
    if (cur == null || typeof cur !== "object") return undefined;
    cur = cur[p];
  }
  return cur;
}

function pickStringByPaths(obj: any, paths: string[]): string | undefined {
  for (const path of paths) {
    const v = getByPath(obj, path);
    if (typeof v === "string") {
      const s = v.trim();
      if (s) return s;
    }
  }
  return undefined;
}

function pickIdByPaths(obj: any, paths: string[]): string | undefined {
  for (const path of paths) {
    const v = getByPath(obj, path);
    if (typeof v === "string") {
      const s = v.trim();
      if (s) return s;
    }
    if (typeof v === "number" && Number.isFinite(v)) return String(v);
  }
  return undefined;
}

function pickNumberByPaths(obj: any, paths: string[]): number | undefined {
  for (const path of paths) {
    const v = getByPath(obj, path);
    if (typeof v === "number" && Number.isFinite(v)) return v;
    if (typeof v === "string" && v.trim() !== "" && !Number.isNaN(Number(v))) return Number(v);
  }
  return undefined;
}

function extractLists(res: any): any[] {
  if (Array.isArray(res)) return res;

  const listsDirect = res && typeof res === "object" ? (res as any).lists : undefined;
  if (Array.isArray(listsDirect)) return listsDirect;

  const listsInEnvelope = getByPath(res, "data.lists");
  if (Array.isArray(listsInEnvelope)) return listsInEnvelope;

  const data = getByPath(res, "data");
  if (Array.isArray(data)) return data;

  return [];
}

function normalizeCharacter(x: any): CharacterVM | null {
  const id = (pickIdByPaths(x, ["id", "characterId"]) ?? "").trim();
  const name = (pickStringByPaths(x, ["name", "characterName"]) ?? "").trim();
  if (!id || !name) return null;

  const description = pickStringByPaths(x, ["description"]) ?? "";
  const voiceId = pickIdByPaths(x, ["voice.id", "voiceId"]) ?? undefined;
  const voiceName =
    pickStringByPaths(x, ["voice.name", "voice.label", "voiceName", "voiceDisplayName"]) ?? undefined;

  const voiceFix = pickNumberByPaths(x, ["voiceFix"]) ?? undefined;

  // iconImageがある場合はAPI_BASE_URLを付加
  const iconImage = pickStringByPaths(x, [
    "iconImage",
    "data.iconImage",
    "character.iconImage",
    "data.character.iconImage",
    "iconUrl",
  ]);
  const iconUrl = resolveBackendAssetUrl(iconImage);

  return {
    id,
    name,
    description,
    initial: name.slice(0, 1).toUpperCase(),
    voiceId: voiceId ? String(voiceId).trim() : undefined,
    voiceName,
    voiceFix,
    iconUrl,
  };
}

function normalizeVoice(x: any): VoiceVM | null {
  const id = (pickIdByPaths(x, ["id"]) ?? "").trim();
  const name = (pickStringByPaths(x, ["name", "label", "displayName"]) ?? "").trim();
  if (!id || !name) return null;
  return { id, name };
}

export default function CharacterSwitch() {
  const [loading, setLoading] = useState(true);
  const [savingCharacterId, setSavingCharacterId] = useState<string | null>(null);
  const [savingVoiceId, setSavingVoiceId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [characters, setCharacters] = useState<CharacterVM[]>([]);
  const [voices, setVoices] = useState<VoiceVM[]>([]);

  const [currentCharacterId, setCurrentCharacterId] = useState<string | null>(null);
  const [currentVoiceId, setCurrentVoiceId] = useState<string | null>(null);

  const pushDebug = (_d: Omit<DebugInfo, "time">) => {};

  const busy = !!savingCharacterId || !!savingVoiceId;

  // ★追加：現在選択中セクションへのスクロール用
  const currentSectionRef = useRef<HTMLElement | null>(null);
  const scrollToCurrent = () => {
    const el = currentSectionRef.current;
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  useEffect(() => {
    let alive = true;

    (async () => {
      setLoading(true);
      setError(null);

      pushDebug({
        phase: "mount",
        auth: storage.getAuth(),
        device: getDeviceParams(),
        note: "CharacterSwitch mounted; calling GET /character and GET /voice",
      });

      try {
        const auth = storage.getAuth();
        const storedCharacterId = (auth.currentCharacterId ?? "").trim() || null;
        const storedVoiceId = (auth.currentVoiceId ?? "").trim() || null;

        pushDebug({
          phase: "before_fetch",
          auth,
          device: getDeviceParams(),
          note: `storage currentCharacterId=${storedCharacterId ?? "null"}, currentVoiceId=${
            storedVoiceId ?? "null"
          }`,
        });

        const [resCharacter, resVoice] = await Promise.all([api.getCharacterList(), api.getVoiceList()]);
        if (!alive) return;

        const rawCharacters = extractLists(resCharacter);
        const rawVoices = extractLists(resVoice);

        const charList = rawCharacters.map(normalizeCharacter).filter(Boolean) as CharacterVM[];
        const voiceList = rawVoices.map(normalizeVoice).filter(Boolean) as VoiceVM[];

        setCharacters(charList);
        setVoices(voiceList);

        const nextCurrentCharacterId =
          storedCharacterId && charList.some((c) => c.id === storedCharacterId) ? storedCharacterId : null;

        let nextCurrentVoiceId =
          storedVoiceId && voiceList.some((v) => v.id === storedVoiceId) ? storedVoiceId : null;

        if (!nextCurrentVoiceId && nextCurrentCharacterId) {
          const cc = charList.find((c) => c.id === nextCurrentCharacterId);
          const vc = cc?.voiceId;
          if (vc && voiceList.some((v) => v.id === vc)) nextCurrentVoiceId = vc;
        }

        setCurrentCharacterId(nextCurrentCharacterId);
        setCurrentVoiceId(nextCurrentVoiceId);

        pushDebug({
          phase: "state_ready",
          auth: storage.getAuth(),
          device: getDeviceParams(),
          normalized: {
            extractedCharacters: rawCharacters.length,
            extractedVoices: rawVoices.length,
            normalizedCharacters: charList.length,
            normalizedVoices: voiceList.length,
            storedCharacterId,
            storedVoiceId,
            matchedCharacter: nextCurrentCharacterId,
            matchedVoice: nextCurrentVoiceId,
          },
          note: "normalized & matched by storage",
        });
      } catch (e) {
        logError(e);
        if (!alive) return;

        const msg = toUserMessage(e);
        setError(msg);

        pushDebug({
          phase: "fetch_error",
          auth: storage.getAuth(),
          device: getDeviceParams(),
          note: `error: ${msg}`,
        });
      } finally {
        if (!alive) return;
        setLoading(false);
        pushDebug({
          phase: "done",
          auth: storage.getAuth(),
          device: getDeviceParams(),
        });
      }
    })();

    return () => {
      alive = false;
    };
  }, []);

  const voiceNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const v of voices) map.set(v.id, v.name);
    return map;
  }, [voices]);

  const currentCharacter = useMemo(() => {
    if (!currentCharacterId) return null;
    return characters.find((c) => c.id === currentCharacterId) ?? null;
  }, [characters, currentCharacterId]);

  const voiceLocked = useMemo(() => {
    if (!currentCharacter) return false;
    return currentCharacter.voiceFix === 1;
  }, [currentCharacter]);

  // 一覧用（現在選択中は除外）
  const listCharacters = useMemo(() => {
    if (!currentCharacterId) return characters;
    return characters.filter((c) => c.id !== currentCharacterId);
  }, [characters, currentCharacterId]);

  const canShowList = !loading && !error && characters.length > 0;
  const canShowCurrent = canShowList && !!currentCharacter;

  const onSelectCharacter = async (c: CharacterVM) => {
    if (busy) return;
    if (!c.voiceId) {
      setError("選択したキャラクターに音声が設定されていません。");
      return;
    }

    const prev = storage.getAuth();
    const prevCharacterId = (prev.currentCharacterId ?? "").trim() || null;
    const prevVoiceId = (prev.currentVoiceId ?? "").trim() || null;

    const nextCharacterId = c.id;
    const nextVoiceId = c.voiceId;

    setSavingCharacterId(c.id);
    setError(null);

    try {
      storage.setAuth({
        currentCharacterId: nextCharacterId,
        currentVoiceId: nextVoiceId,
      });

      await api.updateUserSelection({
        characterId: nextCharacterId,
        voiceId: nextVoiceId,
      });

      setCurrentCharacterId(nextCharacterId);
      setCurrentVoiceId(nextVoiceId);

      // ★追加：切替完了後、現在選択中の位置へスクロール
      requestAnimationFrame(() => scrollToCurrent());
    } catch (e) {
      logError(e);
      const msg = toUserMessage(e);
      setError(msg);

      storage.setAuth({
        currentCharacterId: prevCharacterId ?? undefined,
        currentVoiceId: prevVoiceId ?? undefined,
      });
      setCurrentCharacterId(prevCharacterId);
      setCurrentVoiceId(prevVoiceId);
    } finally {
      setSavingCharacterId(null);
    }
  };

  const onSelectVoice = async (voiceId: string) => {
    if (voiceLocked) return;
    if (busy) return;

    const prev = storage.getAuth();
    const prevVoiceId = (prev.currentVoiceId ?? "").trim() || null;

    const nextVoiceId = voiceId;

    setSavingVoiceId(voiceId);
    setError(null);

    try {
      storage.setAuth({ currentVoiceId: nextVoiceId });

      await api.updateUserSelection({ voiceId: nextVoiceId });

      setCurrentVoiceId(nextVoiceId);
    } catch (e) {
      logError(e);
      const msg = toUserMessage(e);
      setError(msg);

      storage.setAuth({ currentVoiceId: prevVoiceId ?? undefined });
      setCurrentVoiceId(prevVoiceId);
    } finally {
      setSavingVoiceId(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDF8F5] text-[#4A3E39] antialiased">
      <div className="min-h-screen flex flex-col">
        <header className="sticky top-0 z-10 bg-[#FDF8F5]/95 backdrop-blur border-b border-[#EADBC8]">
          <div className="mx-auto w-full max-w-md px-4">
            <div className="h-14 flex items-center justify-between">
              <Link to="/home" className="text-sm font-bold text-[#A67C52] hover:text-[#7C5A4E]">
                ← 戻る
              </Link>
              <p className="text-sm font-bold tracking-tight text-[#7C5A4E]">キャラクター切替</p>
              <span className="text-xs text-[#9C867D]">WizMate</span>
            </div>
          </div>
        </header>

        <main className="flex-1 mx-auto w-full max-w-md px-4 py-6 space-y-6">
          {/* 現在選択中 */}
          <section
            ref={currentSectionRef as any}
            className="scroll-mt-20 rounded-[2.2rem] border border-[#EADBC8] bg-white/70 shadow-[0_10px_25px_rgba(166,124,82,0.12)] overflow-hidden"
          >
            <div className="p-6">
              <div className="flex items-center justify-between">
                <p className="text-xs font-bold text-[#7C5A4E]">現在選択中</p>
                <span className="inline-flex items-center rounded-full bg-[#D1907B]/20 px-2 py-0.5 text-[11px] font-bold text-[#8E6E60]">
                  ACTIVE
                </span>
              </div>

              {loading && <p className="mt-3 text-xs text-[#7B6861]">読み込み中…</p>}
              {!loading && error && <p className="mt-3 text-xs text-[#AF3B2E]">{error}</p>}

              {!loading && !error && !canShowCurrent && (
                <p className="mt-3 text-xs text-[#7B6861]">現在選択中のキャラクターが見つかりません。</p>
              )}

              {canShowCurrent && currentCharacter && (
                <>
                  <div className="mt-4 flex items-center gap-4">
                    <div className="relative">
                      <div className="h-14 w-14 rounded-full border border-[#EADBC8] bg-white shadow-sm overflow-hidden">
                        <img
                          src={currentCharacter.iconUrl || "/img/no-image.png"}
                          alt={currentCharacter.name}
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            if (target.src !== "/img/no-image.png") {
                              target.src = "/img/no-image.png";
                            }
                          }}
                          className="h-full w-full object-cover object-top"
                        />
                      </div>
                      <span className="absolute -right-1 -bottom-1 h-4 w-4 rounded-full border-2 border-white bg-[#D1907B]" />
                    </div>

                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-bold text-[#4A3E39]">{currentCharacter.name}</p>
                      <p className="mt-0.5 text-xs text-[#7B6861] truncate">{currentCharacter.description}</p>
                    </div>
                  </div>

                  <div className="mt-5 rounded-2xl border border-[#EADBC8] bg-white/60 p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs font-bold text-[#7C5A4E]">音声</p>
                        <p className="mt-1 text-[11px] text-[#7B6861]">試聴してから選べます</p>
                      </div>
                      <span className="text-[11px] text-[#7B6861]">タップで変更</span>
                    </div>

                    {voiceLocked && (
                      <p className="mt-2 text-[11px] text-[#7B6861]">
                        ※ このキャラクターは音声を変更できません
                      </p>
                    )}

                    <div className="mt-3 grid grid-cols-1 gap-2 max-h-60 overflow-y-auto pr-1">
                      {voices.map((v) => {
                        const disabled = busy || voiceLocked;
                        const checked = v.id === currentVoiceId;
                        const savingThis = savingVoiceId === v.id;

                        return (
                          <label
                            key={v.id}
                            className={`flex items-center justify-between rounded-2xl border px-4 py-3 text-sm transition ${
                              checked
                                ? "border-[#D1907B] bg-white shadow-sm"
                                : "border-[#EADBC8] bg-white hover:bg-[#FEFBF9]"
                            } ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}
                          >
                            <div className="min-w-0">
                              <div className="flex items-center gap-2 font-bold text-[#4A3E39]">
                                <span className="truncate">{v.name}</span>
                                {checked && (
                                  <span className="rounded-full bg-[#D1907B] px-2 py-0.5 text-[10px] font-bold text-white">
                                    選択中
                                  </span>
                                )}
                                {savingThis && <span className="text-xs text-[#7B6861]">保存中…</span>}
                              </div>
                              <div className="mt-2 flex items-center gap-2">
                                <button
                                  type="button"
                                  disabled={true}
                                  className="inline-flex items-center gap-1 rounded-full border border-[#EADBC8] bg-white px-3 py-1 text-[11px] font-bold text-[#7B6861]"
                                  aria-label={`${v.name}の試聴`}
                                >
                                  ▶ 試聴
                                </button>
                                <span className="text-[11px] text-[#7B6861]">準備中</span>
                              </div>
                            </div>

                            <input
                              type="radio"
                              name="voice_selected"
                              className="h-4 w-4 accent-[#5D4D46]"
                              checked={checked}
                              disabled={disabled}
                              onChange={() => onSelectVoice(v.id)}
                            />
                          </label>
                        );
                      })}
                    </div>
                  </div>
                </>
              )}
            </div>
          </section>

          {/* キャラクター一覧（現在選択中は表示しない） */}
          <section className="rounded-[2.2rem] border border-[#EADBC8] bg-white/70 shadow-[0_10px_25px_rgba(166,124,82,0.12)] overflow-hidden">
            <div className="px-6 py-5 border-b border-[#EADBC8]">
              <h1 className="text-sm font-bold tracking-tight text-[#4A3E39]">キャラクター一覧</h1>
              <p className="mt-1 text-xs text-[#7B6861]">タップして切り替え</p>
            </div>

            <div className="p-2 max-h-[60vh] overflow-y-auto pr-1">
              {canShowList &&
                listCharacters.map((c) => {
                  const voiceLabel = c.voiceName ?? (c.voiceId ? voiceNameById.get(c.voiceId) ?? "—" : "—");
                  const savingThis = savingCharacterId === c.id;

                  return (
                    <button
                      key={c.id}
                      type="button"
                      disabled={busy}
                      onClick={() => onSelectCharacter(c)}
                      className={`w-full text-left rounded-2xl border border-transparent px-4 py-4 transition hover:bg-[#FEFBF9] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D1907B]/20 active:scale-[0.99] ${
                        busy ? "opacity-60 cursor-not-allowed" : ""
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="relative shrink-0">
                          <div className="h-11 w-11 rounded-full border border-[#EADBC8] bg-white shadow-sm overflow-hidden">
                            <img
                              src={c.iconUrl || "/img/no-image.png"}
                              alt={c.name}
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                if (target.src !== "/img/no-image.png") {
                                  target.src = "/img/no-image.png";
                                }
                              }}
                              className="h-full w-full object-cover object-top"
                            />
                          </div>
                        </div>

                        <div className="min-w-0 flex-1">
                          <div className="flex items-center justify-between gap-2">
                            <p className="text-sm font-bold text-[#4A3E39] truncate">
                              {c.name}
                              {savingThis && <span className="ml-2 text-xs text-[#7B6861]">保存中…</span>}
                            </p>
                            <span className="shrink-0 rounded-full border border-[#EADBC8] bg-white px-2 py-0.5 text-[11px] font-bold text-[#7C5A4E]">
                              選択
                            </span>
                          </div>
                          <p className="mt-0.5 text-xs text-[#7B6861] truncate">{c.description}</p>

                          <div className="mt-2 inline-flex items-center gap-2 rounded-full border border-[#EADBC8] bg-white px-3 py-1">
                            <span className="text-[11px] text-[#7B6861]">音声</span>
                            <span className="text-[11px] font-bold text-[#4A3E39]">{voiceLabel}</span>
                          </div>
                        </div>
                      </div>
                    </button>
                  );
                })}

              {!loading && !error && canShowList && listCharacters.length === 0 && (
                <p className="px-4 py-6 text-xs text-[#7B6861]">切り替え可能なキャラクターがありません。</p>
              )}

              {!loading && !error && !canShowList && (
                <p className="px-4 py-6 text-xs text-[#7B6861]">キャラクターが取得できませんでした。</p>
              )}
            </div>

            <div className="px-6 py-5 border-t border-[#EADBC8]">
              <p className="text-xs text-[#7B6861]">※ 切替・保存処理を実装中</p>
            </div>
          </section>
        </main>
      </div>
    </div>
  );
}
